import { useEffect, useState } from "react";
import { Search, Menu, X, Bookmark, LogOut, Crown } from "lucide-react";
import Logo from "@/components/Logo";

const NAV_LINKS = [
  { label: "Explore", href: "#explore" },
  { label: "Shows", href: "#shows" },
  { label: "Submit Content", href: "#submit-content" },
  { label: "Contact Us", href: "#contact" },
];

export default function Header({ query, onQueryChange, user, onOpenAuth, onOpenWatchlist, onOpenSubscribe, onSignOut }) {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeSearch = () => {
    setSearchOpen(false);
    onQueryChange("");
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-gold-500/15 bg-ink-950/90 py-2.5 backdrop-blur-xl"
          : "bg-gradient-to-b from-black/80 to-transparent py-4"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-9 lg:flex" data-testid="header-nav">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`header-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative text-[12px] font-medium uppercase tracking-[0.2em] text-stone-300 transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold-500 after:transition-all after:duration-300 hover:text-gold-400 hover:after:w-full"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {(!user || !user.is_premium) && (
            <button
              data-testid="header-upgrade-button"
              onClick={onOpenSubscribe}
              className="hidden rounded-full border border-gold-500/50 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-gold-300 transition-all duration-300 hover:bg-gold-500/10 hover:shadow-[0_0_20px_rgba(212,175,55,0.25)] xl:block"
            >
              Upgrade to Premium @ ₹199/mo
            </button>
          )}
          {searchOpen ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                document.getElementById("shows")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="flex items-center gap-2 rounded-full border border-gold-500/40 bg-ink-900/90 px-4 py-2"
              data-testid="header-search-form"
            >
              <Search className="h-4 w-4 shrink-0 text-gold-400" />
              <input
                autoFocus
                data-testid="header-search-input"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="Search shows..."
                className="w-24 bg-transparent text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none sm:w-44"
              />
              <button type="button" data-testid="header-search-close-button" aria-label="Close search" onClick={closeSearch}>
                <X className="h-4 w-4 text-stone-400 transition-colors hover:text-gold-400" />
              </button>
            </form>
          ) : (
            <button
              data-testid="header-search-button"
              aria-label="Search shows"
              onClick={() => setSearchOpen(true)}
              className="rounded-full border border-stone-700/70 p-2.5 text-stone-300 transition-all duration-300 hover:border-gold-500/60 hover:text-gold-400"
            >
              <Search className="h-4 w-4" />
            </button>
          )}
          {user ? (
            <div className="relative hidden sm:block" data-testid="header-user-chip">
              <button
                data-testid="header-user-menu-button"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2.5 rounded-full border border-gold-500/30 py-1.5 pl-1.5 pr-4 transition-colors duration-300 hover:border-gold-500/60"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-gold-400 to-gold-600 text-sm font-bold text-ink-950">
                  {(user.name || "V").charAt(0).toUpperCase()}
                </span>
                <span className="max-w-[110px] truncate text-[13px] font-medium text-stone-200">{user.name}</span>
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" data-testid="header-user-menu-backdrop" onClick={() => setUserMenuOpen(false)} />
                  <div data-testid="header-user-menu" className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-gold-500/25 bg-[#0c0a07] p-2 shadow-[0_16px_48px_rgba(0,0,0,0.6)]">
                    <button
                      data-testid="header-watchlist-button"
                      onClick={() => { setUserMenuOpen(false); onOpenWatchlist(); }}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-stone-200 transition-colors hover:bg-gold-500/10 hover:text-gold-300"
                    >
                      <Bookmark className="h-4 w-4 text-gold-400" /> My Watchlist
                    </button>
                    {user.is_premium ? (
                      <div data-testid="header-premium-badge" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gold-300">
                        <Crown className="h-4 w-4" /> Premium Member
                      </div>
                    ) : (
                      <button
                        data-testid="header-upgrade-item"
                        onClick={() => { setUserMenuOpen(false); onOpenSubscribe(); }}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gold-300 transition-colors hover:bg-gold-500/10"
                      >
                        <Crown className="h-4 w-4" /> Upgrade to Premium
                      </button>
                    )}
                    <button
                      data-testid="header-signout-button"
                      onClick={() => { setUserMenuOpen(false); onSignOut(); }}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-stone-300 transition-colors hover:bg-red-500/10 hover:text-red-400"
                    >
                      <LogOut className="h-4 w-4" /> Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button
              data-testid="header-signin-button"
              onClick={onOpenAuth}
              className="hidden rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 bg-[length:200%_100%] px-6 py-2.5 text-[12px] font-bold uppercase tracking-[0.18em] text-ink-950 transition-all duration-500 hover:bg-right hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] sm:block"
            >
              Sign In
            </button>
          )}
          <button
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-full border border-stone-700/70 p-2.5 text-stone-200 lg:hidden"
          >
            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="border-t border-gold-500/10 bg-ink-950/95 px-6 py-4 backdrop-blur-xl lg:hidden" data-testid="mobile-menu">
          {(!user || !user.is_premium) && (
            <button
              data-testid="mobile-upgrade-button"
              onClick={() => { setMenuOpen(false); onOpenSubscribe(); }}
              className="flex w-full items-center gap-3 py-3 text-[13px] font-medium uppercase tracking-[0.2em] text-gold-400"
            >
              <Crown className="h-4 w-4" /> Upgrade to Premium @ ₹199/mo
            </button>
          )}
          {user && (
            <button
              data-testid="mobile-watchlist-button"
              onClick={() => { setMenuOpen(false); onOpenWatchlist(); }}
              className="flex w-full items-center gap-3 py-3 text-[13px] font-medium uppercase tracking-[0.2em] text-gold-400"
            >
              <Bookmark className="h-4 w-4" /> My Watchlist
            </button>
          )}
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setMenuOpen(false)}
              className="block py-3 text-[13px] font-medium uppercase tracking-[0.2em] text-stone-200 transition-colors hover:text-gold-400"
            >
              {link.label}
            </a>
          ))}
          {user ? (
            <button
              data-testid="mobile-signout-button"
              onClick={() => { setMenuOpen(false); onSignOut(); }}
              className="mt-3 w-full rounded-full border border-stone-700 py-3 text-[12px] font-bold uppercase tracking-[0.2em] text-stone-300"
            >
              Sign Out
            </button>
          ) : (
            <button
              data-testid="mobile-signin-button"
              onClick={() => { setMenuOpen(false); onOpenAuth(); }}
              className="mt-3 w-full rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 py-3 text-[12px] font-bold uppercase tracking-[0.2em] text-ink-950"
            >
              Sign In
            </button>
          )}
        </div>
      )}
    </header>
  );
}
